import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { LogOut, Menu, Layout, ChevronRight } from "lucide-react";
import UserProfileDisplay from "./UserProfileDisplay";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "./ui/sheet";
import { Separator } from "./ui/separator";

const ResponsiveNavbar = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const location = useLocation();

  // Don't show navigation on login page
  if (location.pathname === "/login") {
    return null;
  }

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  const getHomeRoute = () => {
    if (user?.is_admin) return "/companies";
    if (user?.is_manager) return "/admin";
    return "/situations";
  };

  const closeMenu = () => setIsMenuOpen(false);

  const menuItems = [
    {
      label: "Situácie",
      path: "/situations",
      showFor: ["all"], // This ensures it shows for all user types
    },
    {
      label: "Správa spoločností",
      path: "/companies",
      showFor: ["admin"],
    },
    {
      label: "Admin panel",
      path: "/admin",
      showFor: ["admin", "manager"],
    },
  ];

  const filteredMenuItems = menuItems.filter(
    (item) =>
      item.showFor.includes("all") ||
      (user?.is_admin && item.showFor.includes("admin")) ||
      (user?.is_manager && item.showFor.includes("manager"))
  );

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-[55]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            <Link to={getHomeRoute()} className="flex items-center">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center mr-3">
                <Layout className="text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">Soluteria</span>
            </Link>
          </div>

          {/* Desktop navigation */}
          {user && (
            <div className="hidden md:flex md:items-center md:space-x-6">
              {/* Navigation links for desktop */}
              <nav className="flex space-x-4">
                {filteredMenuItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${
                      location.pathname === item.path
                        ? "bg-gray-100 text-gray-900"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>

              <UserProfileDisplay variant="full" />

              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-sm font-medium"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Odhlásiť sa
              </Button>
            </div>
          )}

          {/* Mobile menu button with user initials */}
          {user && (
            <div className="md:hidden flex items-center space-x-2">
              <UserProfileDisplay variant="initials" />

              <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="h-6 w-6" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="flex flex-col">
                  <SheetHeader>
                    <SheetTitle>Menu</SheetTitle>
                  </SheetHeader>

                  <div className="flex-1 overflow-auto py-6">
                    {user && <UserProfileDisplay variant="mobile" />}

                    <nav className="mt-6 space-y-1">
                      {filteredMenuItems.map((item, index) => (
                        <Button
                          key={index}
                          variant={
                            location.pathname === item.path
                              ? "default"
                              : "ghost"
                          }
                          className="w-full justify-between"
                          onClick={() => {
                            navigate(item.path);
                            closeMenu();
                          }}
                        >
                          <span>{item.label}</span>
                          <ChevronRight className="h-4 w-4 opacity-70" />
                        </Button>
                      ))}
                    </nav>
                  </div>

                  <SheetFooter className="mt-auto">
                    <Separator className="my-4" />
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => {
                        handleLogout();
                        closeMenu();
                      }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Odhlásiť sa
                    </Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ResponsiveNavbar;
